import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import { integrationAPI } from '../api';

// ─── Лента обмена с 1С ───────────────────────────────────────────────────────
//
// Раньше обмен показывали двумя таблицами кодов: entity, local_id, status. По
// ним видно, что события есть, и совсем не видно, ЧТО происходит — ушёл ли
// выпуск, приняла ли его 1С, что она прислала в ответ. Бухгалтер спрашивает
// «а моя накладная дошла?», и ответить нечем.
//
// Здесь одна лента по времени: стрелка направления, имя документа словами, что
// с ним стало и — если встал — из-за чего. Читается сверху вниз, как разговор.

const STATE = {
  ok:      { color: '#22c55e', icon: '✓' },
  waiting: { color: '#f59e0b', icon: '⏳' },
  queued:  { color: 'var(--dim)', icon: '…' },
  skip:    { color: 'var(--dim)', icon: '·' },
  error:   { color: '#ef4444', icon: '✕' },
};

const when = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

export default function ExchangeJournal({ limit = 60, title = 'Что уходило и приходило' }) {
  const [rows, setRows] = useState(null);
  const [only, setOnly] = useState('all');   // all | out | in | problem

  const load = useCallback(() => {
    integrationAPI.getJournal(limit)
      .then(r => setRows(r.data.results || []))
      .catch(() => setRows([]));
  }, [limit]);

  // Обмен идёт сам, без нашего участия — лента должна догонять его
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const shown = (rows || []).filter(r => {
    if (only === 'out') return r.dir === 'out';
    if (only === 'in')  return r.dir === 'in';
    if (only === 'problem') return r.state === 'error' || r.state === 'waiting';
    return true;
  });

  const TAB = (k, label) => (
    <button key={k} onClick={() => setOnly(k)} style={{
      background: only === k ? 'var(--accent)' : 'transparent',
      color: only === k ? '#fff' : 'var(--muted)',
      border: '1px solid ' + (only === k ? 'var(--accent)' : 'var(--border)'),
      borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600,
      cursor: 'pointer', fontFamily: 'inherit',
    }}>{label}</button>
  );

  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '16px 18px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                    flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ color: 'var(--text)', fontWeight: 800, fontSize: 15, flex: 1 }}>
          {t(title)}
        </div>
        {TAB('all', t('Всё'))}
        {TAB('out', t('в 1С →'))}
        {TAB('in', t('← из 1С'))}
        {TAB('problem', t('встало'))}
      </div>

      {rows === null && (
        <div style={{ color: 'var(--dim)', fontSize: 13 }}>{t('Загрузка')}…</div>
      )}
      {rows !== null && shown.length === 0 && (
        <div style={{ color: 'var(--dim)', fontSize: 13, padding: '10px 0' }}>
          {only === 'problem' ? t('Ничего не встало') : t('Обмена пока не было')}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {shown.map(r => {
          const st = STATE[r.state] || STATE.queued;
          return (
            <div key={r.id} style={{
              display: 'flex', gap: 12, alignItems: 'flex-start',
              padding: '9px 0', borderTop: '1px solid var(--border)',
            }}>
              <span style={{
                fontSize: 15, fontWeight: 800, width: 26, flexShrink: 0,
                color: r.dir === 'out' ? 'var(--accent)' : '#8b5cf6',
              }}>{r.dir === 'out' ? '→' : '←'}</span>

              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', color: 'var(--text)',
                               fontSize: 14, fontWeight: 700 }}>
                  {r.title}
                </span>
                {r.what && (
                  <span style={{ display: 'block', color: 'var(--muted)',
                                 fontSize: 12.5, marginTop: 1 }}>
                    {r.what}
                  </span>
                )}
                {r.problem && (
                  <span style={{ display: 'block', color: st.color,
                                 fontSize: 12, marginTop: 3, lineHeight: 1.4 }}>
                    {r.problem}
                  </span>
                )}
              </span>

              <span style={{ textAlign: 'right', flexShrink: 0 }}>
                <span style={{ display: 'block', color: st.color,
                               fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {st.icon} {r.state_text}
                </span>
                <span style={{ display: 'block', color: 'var(--dim)',
                               fontSize: 11.5, marginTop: 2, whiteSpace: 'nowrap' }}>
                  {when(r.when)}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
